import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Card, CardContent, CardMedia, Typography, Grid, IconButton, TextField, Avatar, Chip, Paper, List, ListItem, ListItemText, ClickAwayListener, AppBar, Toolbar, Container, useTheme, useMediaQuery } from '@mui/material';
import { Search, ShoppingCart, User, Heart, Star, ChevronLeft, ChevronRight, Menu, Store } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

function Ecommerce() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isSticky, setIsSticky] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [timeLeft, setTimeLeft] = useState({ days: 2, hours: 10, minutes: 30, seconds: 45 });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [wishlist, setWishlist] = useState([2, 4]); // Product IDs in wishlist
  const searchRef = useRef(null);

  // Dummy data
  const categories = [
    { name: 'Men', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop', subcategories: ['T-Shirts', 'Shirts', 'Jeans', 'Shoes', 'Watches'] },
    { name: 'Women', image: 'https://images.unsplash.com/photo-1583394838336-acd977736f90?w=300&h=200&fit=crop', subcategories: ['Dresses', 'Tops', 'Jeans', 'Shoes', 'Accessories'] },
    { name: 'Electronics', image: 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=300&h=200&fit=crop', subcategories: ['Mobiles', 'Laptops', 'Headphones', 'Smartwatches', 'Accessories'] },
    { name: 'Accessories', image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=200&fit=crop', subcategories: ['Bags', 'Jewelry', 'Belts', 'Sunglasses', 'Watches'] },
    { name: 'Home & Living', image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=300&h=200&fit=crop', subcategories: ['Decor', 'Furniture', 'Kitchen', 'Bedding', 'Lighting'] },
    { name: 'Sports', image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=300&h=200&fit=crop', subcategories: ['Running', 'Gym', 'Yoga', 'Cycling', 'Swimming'] },
  ];

  const smartCategories = [
    { name: '🔥 Trending Now', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=300&h=200&fit=crop', type: 'trending' },
    { name: '💸 Under ₹999', image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=300&h=200&fit=crop', type: 'budget' },
    { name: '⭐ Best Sellers', image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=200&fit=crop', type: 'bestseller' },
    { name: '🆕 New Arrivals', image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=300&h=200&fit=crop', type: 'new' },
  ];

  const products = [
    { id: 1, title: 'Wireless Headphones', price: 100, discount: 20, rating: 4.5, image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop' },
    { id: 2, title: 'Smart Watch', price: 200, discount: 15, rating: 4.0, image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=300&fit=crop' },
    { id: 3, title: 'Running Shoes', price: 150, discount: 10, rating: 4.8, image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=300&h=300&fit=crop' },
    { id: 4, title: 'Laptop', price: 250, discount: 25, rating: 3.5, image: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=300&h=300&fit=crop' },
  ];

  const testimonials = [
    { name: 'John Doe', review: 'Great products!', rating: 5, image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face' },
    { name: 'Jane Smith', review: 'Excellent service.', rating: 4, image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face' },
  ];

  const slides = [
    'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=400&fit=crop',
    'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1200&h=400&fit=crop',
  ];

  // All products for search suggestions
  const allProducts = [
    { id: 1, title: 'Wireless Headphones', price: 100, discount: 20, rating: 4.5, category: 'Electronics', image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop' },
    { id: 2, title: 'Smart Watch', price: 200, discount: 15, rating: 4.0, category: 'Electronics', image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=300&fit=crop' },
    { id: 3, title: 'Running Shoes', price: 150, discount: 10, rating: 4.8, category: 'Sports', image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=300&h=300&fit=crop' },
    { id: 4, title: 'Laptop', price: 250, discount: 25, rating: 3.5, category: 'Electronics', image: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=300&h=300&fit=crop' },
    { id: 5, title: 'Bluetooth Speaker', price: 89, rating: 4.3, category: 'Electronics', image: 'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=300&h=300&fit=crop' },
    { id: 6, title: 'Leather Jacket', price: 200, rating: 4.1, category: 'Men', image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=300&h=300&fit=crop' },
    { id: 7, title: 'Yoga Mat', price: 50, rating: 4.2, category: 'Sports', image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=300&h=300&fit=crop' },
    { id: 8, title: 'Sunglasses', price: 80, rating: 4.1, category: 'Accessories', image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=300&h=300&fit=crop' },
  ];

  // Sticky navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        if (prev.days > 0) return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Smart search functionality
  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.length > 0) {
      const filtered = allProducts.filter(product =>
        product.title.toLowerCase().includes(query.toLowerCase()) ||
        product.category.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 5); // Limit to 5 suggestions
      setSearchSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      navigate(`/ShopPage?search=${encodeURIComponent(searchQuery)}`);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (product) => {
    navigate(`/ProductPage?id=${product.id}`);
    setShowSuggestions(false);
    setSearchQuery('');
  };

  const handleClickAway = () => {
    setShowSuggestions(false);
  };

  const toggleWishlist = (productId) => {
    setWishlist(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <Box sx={{ mt: 7, mx: 2, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Navbar */}
      <AppBar
        position={isSticky ? 'fixed' : 'static'}
        sx={{
          bgcolor: 'white',
          boxShadow: isSticky ? 3 : 1,
          transition: 'all 0.3s',
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Store size={32} color={theme.palette.primary.main} />
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                ShopHub
              </Typography>
            </Box>
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Button onClick={() => navigate('/Ecommerce')} sx={{ color: 'text.primary' }}>Home</Button>
                <Button onClick={() => navigate('/ShopPage')} sx={{ color: 'text.primary' }}>Shop</Button>
                <Button sx={{ color: 'text.primary' }}>Categories</Button>
                <Button sx={{ color: 'text.primary' }}>Deals</Button>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <ClickAwayListener onClickAway={handleClickAway}>
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    ref={searchRef}
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                    size="small"
                    sx={{ width: isMobile ? 200 : 300, '& .MuiOutlinedInput-root': { borderRadius: 25 } }}
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={handleSearchSubmit} size="small">
                          <Search size={20} />
                        </IconButton>
                      ),
                    }}
                  />
                  {showSuggestions && searchSuggestions.length > 0 && (
                    <Paper
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        maxHeight: 300,
                        overflow: 'auto',
                        borderRadius: 2,
                        boxShadow: 3,
                      }}
                    >
                      <List>
                        {searchSuggestions.map((product) => (
                          <ListItem
                            key={product.id}
                            button
                            onClick={() => handleSuggestionClick(product)}
                            sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                              <img
                                src={product.image}
                                alt={product.title}
                                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                              />
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {product.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {product.category} • ${product.price}
                                </Typography>
                              </Box>
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Box>
              </ClickAwayListener>
              <IconButton onClick={() => navigate('/CartPage')} sx={{ color: 'text.primary' }}>
                <ShoppingCart />
              </IconButton>
              <IconButton onClick={() => navigate('/UserDashboard')} sx={{ color: 'text.primary' }}>
                <User />
              </IconButton>
              {isMobile && (
                <IconButton sx={{ color: 'text.primary' }}>
                  <Menu />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Banner */}
      <Box sx={{ position: 'relative', mt: isSticky ? '64px' : 0, height: '500px', overflow: 'hidden' }}>
        <Box
          component="img"
          src={slides[currentSlide]}
          alt="Hero"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 100%)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: 'white', zIndex: 1 }}>
          <Typography
            variant="h2"
            component={motion.div}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{ fontWeight: 'bold', mb: 2, textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
          >
            Summer Sale 50% OFF
          </Typography>
          <Typography
            variant="h5"
            component={motion.div}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            sx={{ mb: 4, textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
          >
            Discover amazing deals on top brands
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: 25,
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
            component={motion.button}
            whileHover={{ scale: 1.05, boxShadow: '0 8px 25px rgba(0,0,0,0.3)' }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/ShopPage')}
          >
            Shop Now
          </Button>
        </Container>
        <IconButton
          onClick={prevSlide}
          sx={{
            position: 'absolute',
            left: 20,
            top: '50%',
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.5)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
          }}
        >
          <ChevronLeft />
        </IconButton>
        <IconButton
          onClick={nextSlide}
          sx={{
            position: 'absolute',
            right: 20,
            top: '50%',
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.5)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Categories Grid */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            mb: 4,
            textAlign: 'center',
            fontWeight: 'bold',
            color: 'text.primary'
          }}
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Shop by Category
        </Typography>
        <Grid container spacing={3}>
          {categories.map((cat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                component={motion.div}
                whileHover={{ scale: 1.05, y: -8 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 300 }}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: 2,
                  '&:hover': { boxShadow: 6 }
                }}
                onClick={() => navigate('/ShopPage')}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={cat.image}
                    alt={cat.name}
                    sx={{ transition: 'transform 0.3s', '&:hover': { transform: 'scale(1.1)' } }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      p: 2,
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{cat.name}</Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Trending Products */}
      <Box sx={{ py: 8, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              mb: 6,
              textAlign: 'center',
              fontWeight: 'bold',
              color: 'text.primary'
            }}
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Trending Products
          </Typography>
          <Grid container spacing={4}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <Card
                  component={motion.div}
                  whileHover={{ y: -16, scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: 3,
                    '&:hover': { boxShadow: 10 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onClick={() => navigate('/ProductPage')}
                >
                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      height="350"
                      image={product.image}
                      alt={product.title}
                      sx={{
                        transition: 'transform 0.4s ease',
                        '&:hover': { transform: 'scale(1.08)' },
                        objectFit: 'cover'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        bgcolor: 'rgba(255,255,255,0.9)',
                        borderRadius: '50%',
                        p: 1,
                        boxShadow: 2
                      }}
                    >
                      <IconButton
                        component={motion.button}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleWishlist(product.id);
                        }}
                        sx={{ p: 0 }}
                      >
                        <Heart
                          size={24}
                          fill={wishlist.includes(product.id) ? 'red' : 'none'}
                          color={wishlist.includes(product.id) ? 'red' : 'grey.600'}
                        />
                      </IconButton>
                    </Box>
                    <Chip
                      label={`${product.discount}% OFF`}
                      color="error"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        fontWeight: 'bold',
                        bgcolor: 'error.main',
                        color: 'white'
                      }}
                    />
                  </Box>
                  <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, lineHeight: 1.3 }}>
                      {product.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                        ${product.price - (product.price * product.discount / 100)}
                      </Typography>
                      <Typography variant="body1" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                        ${product.price}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3 }}>
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={18} fill={i < Math.floor(product.rating) ? 'gold' : 'none'} color="gold" />
                      ))}
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({product.rating})
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        borderRadius: 3,
                        py: 1.5,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        fontSize: '1rem',
                        mt: 'auto',
                        bgcolor: 'primary.main',
                        '&:hover': { bgcolor: 'primary.dark', transform: 'translateY(-2px)' },
                        transition: 'all 0.2s'
                      }}
                      component={motion.button}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Add to Cart
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* AI Recommendations */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2 }}
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            🤖 Recommended for You
          </Typography>
          <Chip
            label="AI Powered"
            color="primary"
            size="small"
            sx={{ fontWeight: 'bold', mb: 2 }}
          />
          <Typography variant="body1" color="text.secondary">
            Based on your browsing history and preferences
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {allProducts.slice(0, 4).map((product) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`rec-${product.id}`}>
              <Card
                component={motion.div}
                whileHover={{ y: -16, scale: 1.03 }}
                transition={{ type: "spring", stiffness: 300 }}
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: 3,
                  '&:hover': { boxShadow: 10 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onClick={() => navigate('/ProductPage')}
              >
                <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                  <CardMedia
                    component="img"
                    height="320"
                    image={product.image}
                    alt={product.title}
                    sx={{
                      transition: 'transform 0.4s ease',
                      '&:hover': { transform: 'scale(1.08)' },
                      objectFit: 'cover'
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor: 'rgba(255,255,255,0.9)',
                      borderRadius: '50%',
                      p: 1,
                      boxShadow: 2
                    }}
                  >
                    <IconButton
                      component={motion.button}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleWishlist(product.id);
                      }}
                      sx={{ p: 0 }}
                    >
                      <Heart
                        size={24}
                        fill={wishlist.includes(product.id) ? 'red' : 'none'}
                        color={wishlist.includes(product.id) ? 'red' : 'grey.600'}
                      />
                    </IconButton>
                  </Box>
                  <Chip
                    label="Recommended"
                    color="secondary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      fontWeight: 'bold',
                      bgcolor: 'secondary.main',
                      color: 'white'
                    }}
                  />
                </Box>
                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, lineHeight: 1.3 }}>
                    {product.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                      ${product.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {product.category}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3 }}>
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={18} fill={i < Math.floor(product.rating) ? 'gold' : 'none'} color="gold" />
                    ))}
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({product.rating})
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderRadius: 3,
                      py: 1.5,
                      fontWeight: 'bold',
                      textTransform: 'none',
                      fontSize: '1rem',
                      mt: 'auto',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s'
                    }}
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Add to Cart
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Offer Banner */}
      <Box
        sx={{
          py: 6,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.1
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 'bold', mb: 2, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}
            component={motion.div}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            Buy 2 Get 1 Free
          </Typography>
          <Typography
            variant="h5"
            sx={{ mb: 4, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}
          >
            Ends in: {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              px: 4,
              py: 1.5,
              borderRadius: 25,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': { bgcolor: 'grey.100' }
            }}
            component={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/ShopPage')}
          >
            Shop Deals Now
          </Button>
        </Container>
      </Box>

      {/* Testimonials */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            mb: 4,
            textAlign: 'center',
            fontWeight: 'bold',
            color: 'text.primary'
          }}
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          What Our Customers Say
        </Typography>
        <Grid container spacing={4}>
          {testimonials.map((test, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                component={motion.div}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  boxShadow: 2,
                  '&:hover': { boxShadow: 6 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar src={test.image} sx={{ width: 60, height: 60 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{test.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} fill={i < test.rating ? 'gold' : 'none'} color="gold" />
                      ))}
                    </Box>
                  </Box>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1 }}>
                  "{test.review}"
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 6, bgcolor: 'grey.900', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Store size={24} color={theme.palette.primary.main} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>ShopHub</Typography>
              </Box>
              <Typography variant="body2" color="grey.400" sx={{ mb: 2 }}>
                Your one-stop shop for all your needs. Quality products at affordable prices.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}>
                  <Typography>FB</Typography>
                </IconButton>
                <IconButton sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}>
                  <Typography>TW</Typography>
                </IconButton>
                <IconButton sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}>
                  <Typography>IG</Typography>
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Quick Links</Typography>
              <Typography variant="body2" sx={{ mb: 1, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Home</Typography>
              <Typography variant="body2" sx={{ mb: 1, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Shop</Typography>
              <Typography variant="body2" sx={{ mb: 1, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Categories</Typography>
              <Typography variant="body2" sx={{ mb: 1, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Contact</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Customer Service</Typography>
              <Typography variant="body2" sx={{ mb: 1, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Help Center</Typography>
              <Typography variant="body2" sx={{ mb: 1, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Shipping Info</Typography>
              <Typography variant="body2" sx={{ mb: 1, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Returns</Typography>
              <Typography variant="body2" sx={{ mb: 1, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Size Guide</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Newsletter</Typography>
              <Typography variant="body2" color="grey.400" sx={{ mb: 2 }}>
                Subscribe to get special offers and updates.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  placeholder="Your email"
                  size="small"
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.800',
                      borderRadius: 1,
                      '& fieldset': { borderColor: 'grey.600' },
                      '&:hover fieldset': { borderColor: 'grey.500' },
                      '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                    },
                    '& .MuiOutlinedInput-input': { color: 'white' }
                  }}
                />
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ borderTop: 1, borderColor: 'grey.800', mt: 4, pt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="grey.400">
              © 2026 ShopHub. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Ecommerce;
